import { useAuth } from "@/core/auth/hooks/useAuth";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { LoadingSpinner } from "@/shared/components/ui/LoadingSpinner";
import { Page, PageContent } from "@/shared/components/ui/Page";
import { PageHeader } from "@/shared/components/ui/PageHeader";
import { AppointmentRulesSection } from "../components/AppointmentRulesSection";
import { useAppointmentRules } from "../hooks/appointmentRuleQueries";
import { useCurrentBusiness } from "../hooks/useCurrentBusiness";

const header = <PageHeader title="Appointment rules" description="Manage customer cancellation, rescheduling and booking notice." />;

function AppointmentRulesLoader({ businessId }: { businessId: string }) {
  const query = useAppointmentRules(businessId);
  if (!query.data) return <Page>{header}<PageContent>{query.error
    ? <ErrorState title="Couldn't load appointment rules" message={query.error.message} onRetry={() => void query.refetch()} />
    : <LoadingSpinner message="Loading appointment rules..." />}</PageContent></Page>;
  return <AppointmentRulesSection header={header} businessId={businessId} rules={query.data} refreshError={query.error?.message}
    onRetry={() => void query.refetch()} onReload={async () => {
      const result = await query.refetch();
      if (result.error) throw result.error;
      if (!result.data) throw new Error("No appointment rules were returned.");
      return result.data;
    }} />;
}

export function AppointmentRulesPage() {
  const { user } = useAuth();
  const { business, businessId, loading } = useCurrentBusiness();
  if (!loading && business && businessId && user && business.ownerId === user.id) {
    return <AppointmentRulesLoader key={`${user.id}:${businessId}`} businessId={businessId} />;
  }
  return (
    <Page>
      {header}
      <PageContent>
        {loading ? <LoadingSpinner message="Loading business..." /> : !business || !businessId
          ? <ErrorState message="Select a business to manage appointment rules." />
          : <ErrorState message="Only the business owner can manage appointment rules." />}
      </PageContent>
    </Page>
  );
}
